import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";

const NAVY = "#16213E";
const PRIORITES = [
  ["haute", "Haute"],
  ["moyenne", "Moyenne"],
  ["faible", "Faible"],
];
const FIABILITES = { verifie: "Vérifié", partiellement_verifie: "Partiellement vérifié", a_confirmer: "À confirmer" };
const PRIO_COLOR = { haute: "red", moyenne: "orange", faible: "gray" };

const toLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const fromLines = (arr) => (arr || []).join("\n");

// Édition en ligne multi-lignes : ressemble au texte, s'ouvre au clic en une
// zone qui s'ajuste automatiquement à la hauteur du contenu (tout le texte visible).
function TextEdit({ value, onChange, placeholder, ...rest }) {
  const [editing, setEditing] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (editing && el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [editing, value]);

  if (!editing) {
    return (
      <Box
        onClick={() => setEditing(true)}
        cursor="text"
        _hover={{ bg: "blackAlpha.50" }}
        borderRadius="4px"
        px="5px"
        mx="-5px"
        minH="1.5em"
        whiteSpace="pre-wrap"
        color={value ? "inherit" : "gray.400"}
        {...rest}
      >
        {value || placeholder}
      </Box>
    );
  }
  return (
    <Textarea
      ref={ref}
      autoFocus
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => setEditing(false)}
      onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
      fontSize="inherit"
      fontWeight="inherit"
      lineHeight="inherit"
      color="inherit"
      p="6px 8px"
      mx="-5px"
      minH="0"
      resize="none"
      overflow="hidden"
      borderColor="teal.300"
      focusBorderColor="teal.400"
      {...rest}
    />
  );
}

function SectionTitle({ n, children }) {
  return (
    <Flex align="center" gap="10px" mt="34px" mb="16px" pb="7px" borderBottom="2px solid" borderColor={NAVY}>
      <Flex align="center" justify="center" bg={NAVY} color="white" borderRadius="6px" minW="26px" h="26px" fontSize="13px" fontWeight="700">{n}</Flex>
      <Heading size="sm" letterSpacing="0.02em" textTransform="uppercase" color={NAVY}>{children}</Heading>
    </Flex>
  );
}

// Petit bouton supprimer, discret, révélé au survol de la ligne parente (role=group).
function DeleteBtn({ onClick }) {
  return (
    <IconButton
      aria-label="Supprimer"
      icon={<FaTrash />}
      size="xs"
      variant="ghost"
      color="red.300"
      flexShrink={0}
      opacity={0}
      _groupHover={{ opacity: 1 }}
      onClick={onClick}
    />
  );
}

export default function MatriceEditor({ isOpen, onClose, contenu, onSave, saving }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    if (isOpen && contenu) setM(structuredClone(contenu));
  }, [isOpen, contenu]);
  if (!m) return null;

  const edit = (fn) => setM((prev) => { const n = structuredClone(prev); fn(n); return n; });
  const dossiers = m.dossiers || [];
  const groupes = m.groupes_personnes || [];
  const familles = m.familles || [];
  const tags = m.tags || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="16px" maxH="92vh" bg="gray.100">
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" bg="white" borderTopRadius="16px">
          <Flex justify="space-between" align="center" pr="30px">
            <Text>Matrice de veille — édition</Text>
            <Text fontSize="xs" fontWeight="400" color="gray.500">Cliquez sur un texte pour le modifier</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py="28px" bg="gray.100">
          <Box maxW="900px" mx="auto" bg="white" borderRadius="10px" boxShadow="0 1px 10px rgba(0,0,0,0.06)" px={{ base: "22px", md: "56px" }} py={{ base: "26px", md: "44px" }} lineHeight="1.55" color="gray.800">
            {/* En-tête document */}
            <Box textAlign="center" mb="8px">
              <Text fontSize="11px" letterSpacing="0.18em" textTransform="uppercase" color="gray.400" fontWeight="600">Notit IA · Matrice Veille IA</Text>
              <Heading size="lg" color={NAVY} mt="6px" mb="2px">{m.client}</Heading>
              <Text fontSize="sm" color="gray.500">{m.date_edition}</Text>
            </Box>

            {/* 1 — DOSSIERS */}
            <SectionTitle n="1">Dossiers prioritaires</SectionTitle>
            {dossiers.map((d, i) => (
              <Box key={i} role="group" mb="22px" pb="16px" borderBottom={i < dossiers.length - 1 ? "1px dashed" : "none"} borderColor="gray.200">
                <Flex align="flex-start" gap="10px" wrap="wrap">
                  <Box flex="1" minW="240px" fontWeight="700" fontSize="md" color={NAVY}>
                    <TextEdit value={d.intitule} onChange={(v) => edit((n) => { n.dossiers[i].intitule = v; })} placeholder="Intitulé du dossier" />
                  </Box>
                  <Select size="xs" w="auto" variant="filled" borderRadius="full" flexShrink={0} value={d.fiabilite || "a_confirmer"} onChange={(e) => edit((n) => { n.dossiers[i].fiabilite = e.target.value; })}>
                    {Object.entries(FIABILITES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Select>
                  <DeleteBtn onClick={() => edit((n) => { n.dossiers.splice(i, 1); })} />
                </Flex>
                <Text fontSize="xs" color="gray.500" fontStyle="italic">
                  <TextEdit value={d.sigle} onChange={(v) => edit((n) => { n.dossiers[i].sigle = v; })} placeholder="sigle" />
                </Text>
                <Box mt="8px" fontSize="sm">
                  <TextEdit value={d.description} onChange={(v) => edit((n) => { n.dossiers[i].description = v; })} placeholder="Description" />
                </Box>
                <Box mt="10px">
                  <Text fontSize="xs" fontWeight="700" color={NAVY} textTransform="uppercase" letterSpacing="0.04em" mb="2px">Enjeux pour {m.client}</Text>
                  <Box fontSize="sm">
                    <TextEdit value={fromLines(d.enjeux_client)} onChange={(v) => edit((n) => { n.dossiers[i].enjeux_client = toLines(v); })} placeholder="Un enjeu par ligne" />
                  </Box>
                </Box>
                {(d.etapes || []).length > 0 && (
                  <Box mt="10px" pl="12px" borderLeft="2px solid" borderColor="gray.200">
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.05em" mb="3px">Étapes procédurales · sources vérifiées (lecture seule)</Text>
                    {d.etapes.map((et, k) => (
                      <Text key={k} fontSize="xs" color="gray.600" mb="1px"><b>{et.institution}</b> — {et.etape}{et.date ? ` (${et.date})` : ""}</Text>
                    ))}
                  </Box>
                )}
              </Box>
            ))}

            {/* 2 — INSTRUCTIONS */}
            {m.instructions && (
              <>
                <SectionTitle n="2">Instructions de rédaction</SectionTitle>
                {(m.instructions.sections || []).map((s, i) => (
                  <Box key={i} mb="13px">
                    <Box fontWeight="700" fontSize="sm" color={NAVY}><TextEdit value={s.nom} onChange={(v) => edit((n) => { n.instructions.sections[i].nom = v; })} placeholder="Section" /></Box>
                    <Box fontSize="sm" color="gray.700" mt="1px"><TextEdit value={s.definition} onChange={(v) => edit((n) => { n.instructions.sections[i].definition = v; })} placeholder="Définition" /></Box>
                  </Box>
                ))}
                <Box mt="14px">
                  <Text fontSize="xs" fontWeight="700" color={NAVY} textTransform="uppercase" letterSpacing="0.04em" mb="2px">Style de rédaction</Text>
                  <Box fontSize="sm" color="gray.700"><TextEdit value={m.instructions.regle_style_client} onChange={(v) => edit((n) => { n.instructions.regle_style_client = v; })} placeholder="Règle de style propre au client" /></Box>
                </Box>
              </>
            )}

            {/* 3 — MOTS-CLÉS */}
            <SectionTitle n="3">Mots-clés</SectionTitle>
            {familles.map((f, i) => (
              <Box key={i} role="group" mb="14px">
                <Flex align="center" gap="8px">
                  <Box flex="1" fontWeight="700" fontSize="sm" color={NAVY}><TextEdit value={f.titre} onChange={(v) => edit((n) => { n.familles[i].titre = v; })} placeholder="Famille" /></Box>
                  <DeleteBtn onClick={() => edit((n) => { n.familles.splice(i, 1); })} />
                </Flex>
                <Box fontSize="sm" color="gray.700">
                  <TextEdit value={fromLines(f.termes)} onChange={(v) => edit((n) => { n.familles[i].termes = toLines(v); })} placeholder="Un terme par ligne" />
                </Box>
              </Box>
            ))}

            {/* 4 — PERSONNES */}
            <SectionTitle n="4">Personnes clés</SectionTitle>
            {groupes.map((g, gi) => (
              <Box key={gi} mb="18px">
                <Text fontWeight="700" fontSize="sm" color={NAVY} mb="4px" pb="3px" borderBottom="1px solid" borderColor="gray.200">
                  <TextEdit value={g.titre} onChange={(v) => edit((n) => { n.groupes_personnes[gi].titre = v; })} placeholder="Groupe" />
                </Text>
                {(g.personnes || []).map((p, pi) => (
                  <Flex key={pi} role="group" gap="10px" align="flex-start" fontSize="sm" py="2px">
                    <Box w={{ base: "130px", md: "195px" }} flexShrink={0} fontWeight="600"><TextEdit value={p.nom} onChange={(v) => edit((n) => { n.groupes_personnes[gi].personnes[pi].nom = v; })} placeholder="Nom" /></Box>
                    <Box flex="1" color="gray.600"><TextEdit value={p.fonction} onChange={(v) => edit((n) => { n.groupes_personnes[gi].personnes[pi].fonction = v; })} placeholder="Fonction" /></Box>
                    <Select size="xs" w="auto" flexShrink={0} variant="filled" borderRadius="full" value={p.priorite || "moyenne"} onChange={(e) => edit((n) => { n.groupes_personnes[gi].personnes[pi].priorite = e.target.value; })}>
                      {PRIORITES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </Select>
                    <DeleteBtn onClick={() => edit((n) => { n.groupes_personnes[gi].personnes.splice(pi, 1); })} />
                  </Flex>
                ))}
              </Box>
            ))}

            {/* 5 — TAGS */}
            <SectionTitle n="5">Tags des thèmes clés</SectionTitle>
            {tags.map((t, i) => (
              <Flex key={i} gap="10px" mb="10px" align="flex-start">
                <Text color={NAVY} fontWeight="800" fontSize="sm" mt="1px">{i + 1}</Text>
                <Box flex="1">
                  <Flex align="center" gap="8px">
                    <Box flex="1" fontWeight="700" fontSize="sm" color={NAVY}><TextEdit value={t.nom} onChange={(v) => edit((n) => { n.tags[i].nom = v; })} placeholder="Tag" /></Box>
                    {t.transversal && <Badge colorScheme="purple" fontSize="9px" flexShrink={0}>transversal</Badge>}
                  </Flex>
                  <Box fontSize="sm" color="gray.700"><TextEdit value={t.definition} onChange={(v) => edit((n) => { n.tags[i].definition = v; })} placeholder="Définition" /></Box>
                </Box>
              </Flex>
            ))}
          </Box>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.200" bg="white" borderBottomRadius="16px">
          <Button variant="ghost" onClick={onClose} mr={3}>Annuler</Button>
          <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} isLoading={saving} loadingText="Enregistrement…" onClick={() => onSave(m)}>
            Enregistrer & régénérer le .docx
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
